import React, { useState, useMemo, useCallback, useRef, useEffect } from 'react';
import { X, Users, UserPlus, Check, Calendar, Lock } from 'lucide-react';
import api from '../../../../services/api';
import { unassignSection, bulkAssignSections } from '../../services/sectionService';
import { formatForDateInput } from '../analysis/utils/helperUtils';
import CustomLoader from '../../../../shared/components/ui/CustomLoader';
import { Avatar } from '../../../dashboard/components/DashboardComponents';
import './SectionAssignModal.css';

/**
 * Premium Modal component for Bulk Assignment of RFP sections.
 * Features local staging and transactional API submission.
 */
const SectionAssignModal = ({
    isOpen,
    onClose,
    assignments,
    setAssignments,
    rfpId,
    editableData,
    teamMembers,
    fetchOverview,
    fetchRFPData,
    setSuccessToast
}) => {
    // Local Staging state
    const [stagedAssignments, setStagedAssignments] = useState({}); // { sectionId: [memberObj, ...] }
    const [stagedDeadlines, setStagedDeadlines] = useState({});     // { sectionId: dateString }

    const proposalDateStr = formatForDateInput(editableData?.PROPOSAL_DEADLINE || editableData?.proposal_deadline || "");
    const maxDeadlineDate = useMemo(() => {
        if (!proposalDateStr) return undefined;
        const [year, month, day] = proposalDateStr.split('-');
        if (!year || !month || !day) return undefined;
        const propDate = new Date(Number(year), Number(month) - 1, Number(day));
        propDate.setDate(propDate.getDate() - 1);

        // Format back to YYYY-MM-DD
        const y = propDate.getFullYear();
        const m = String(propDate.getMonth() + 1).padStart(2, '0');
        const d = String(propDate.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    }, [proposalDateStr]);

    const [showDropdownForId, setShowDropdownForId] = useState(null);
    const [isAssigning, setIsAssigning] = useState(false);

    const selectRef = useRef(null);

    useEffect(() => {
        if (showDropdownForId && selectRef.current) {
            try {
                if (selectRef.current.showPicker) {
                    selectRef.current.showPicker();
                }
            } catch (error) {
                console.warn("showPicker failed or not supported:", error);
            }
        }
    }, [showDropdownForId]);

    // Unassign Confirmation State
    const [unassignModal, setUnassignModal] = useState({
        show: false,
        sectionId: null,
        sectionLabel: null,
        memberId: null,
        memberName: null,
        isExisting: false
    });

    const membersList = teamMembers || [];
    const sections = editableData?.sections || [];

    // Helper to add a member to the local staged list
    const handleAddMember = useCallback((section, member) => {
        if (!member) return;
        const memberId = member._id || member.id;
        const memberName = member.name || `${member.first_name} ${member.last_name}`;
        const sectionId = section.id || section._id;

        // Find a default deadline from other members of this section if available
        const sectionLabel = section.label || section.key;
        const sectionKey = section.key || section.label;
        const existingRaw = assignments[sectionLabel] || assignments[sectionKey] || assignments[sectionId] ||
            assignments[sectionLabel.toLowerCase()] || assignments[sectionKey.toLowerCase()] || [];

        let defaultDeadline = "";
        if (existingRaw.length > 0) {
            defaultDeadline = formatForDateInput(existingRaw[0].deadline || "");
        }
        if (!defaultDeadline) {
            // Try to find a staged deadline of another member in this section
            const siblingKey = Object.keys(stagedDeadlines).find(k => k.startsWith(`${sectionId}-`));
            if (siblingKey) {
                defaultDeadline = stagedDeadlines[siblingKey];
            }
        }

        setStagedAssignments(prev => {
            const current = prev[sectionId] || [];
            // Avoid duplicates in staged list
            if (current.some(m => m.id === memberId)) return prev;
            return {
                ...prev,
                [sectionId]: [...current, { id: memberId, name: memberName }]
            };
        });

        // Set default deadline if we found one
        if (defaultDeadline) {
            setStagedDeadlines(prev => ({
                ...prev,
                [`${sectionId}-${memberId}`]: defaultDeadline
            }));
        }

        setShowDropdownForId(null);
    }, [assignments, stagedDeadlines]);

    // Handle the "X" click on a member pill
    const handleUnassignClick = (sectionId, sectionLabel, member) => {
        const memberId = member.id || member._id;
        const memberName = member.name;

        if (member.isExisting) {
            // Show confirmation modal for existing assignments
            setUnassignModal({
                show: true,
                sectionId,
                sectionLabel,
                memberId,
                memberName,
                isExisting: true
            });
        } else {
            // Just remove from staged list instantly
            setStagedAssignments(prev => {
                const updated = (prev[sectionId] || []).filter(m => (m.id || m._id) !== memberId);
                const newState = { ...prev };
                if (updated.length === 0) {
                    delete newState[sectionId];
                } else {
                    newState[sectionId] = updated;
                }
                return newState;
            });
            // Clean up staged deadlines for this removed staging member
            setStagedDeadlines(prev => {
                const newState = { ...prev };
                delete newState[`${sectionId}-${memberId}`];
                return newState;
            });
        }
    };

    const handleConfirmUnassign = async () => {
        const { sectionId, memberId } = unassignModal;
        setIsAssigning(true);
        try {
            const response = await unassignSection(rfpId, sectionId, {
                user_ids: [memberId],
                remove_all: false
            });

            if (response.status === 200 || response.status === 204) {
                // Refresh data
                if (typeof fetchOverview === 'function') {
                    await fetchOverview(setAssignments);
                }
                setUnassignModal({ show: false, sectionId: null, memberId: null, memberName: null });
            }
        } catch (error) {
            console.error('Unassign failed:', error);
            const detail = error.response?.data?.detail || error.response?.data?.message || 'Failed to unassign member. Please try again.';
            alert(typeof detail === 'object' ? JSON.stringify(detail) : detail);
        } finally {
            setIsAssigning(false);
        }
    };

    const handleDeadlineChange = useCallback((sectionId, memberId, date) => {
        if (memberId) {
            setStagedDeadlines(prev => ({ ...prev, [`${sectionId}-${memberId}`]: date }));
        } else {
            setStagedDeadlines(prev => ({ ...prev, [sectionId]: date }));
        }
    }, []);

    // Check if there are any changes to confirm
    const hasChanges = useMemo(() => {
        return Object.keys(stagedAssignments).length > 0 || Object.keys(stagedDeadlines).length > 0;
    }, [stagedAssignments, stagedDeadlines]);

    const canConfirm = useMemo(() => {
        if (!hasChanges) return false;

        // Every newly staged assignment must have a non-empty deadline
        for (const [sectionId, members] of Object.entries(stagedAssignments)) {
            for (const member of members) {
                const key = `${sectionId}-${member.id}`;
                const deadline = stagedDeadlines[key] || stagedDeadlines[sectionId];
                if (!deadline) {
                    return false;
                }
                if (maxDeadlineDate && deadline > maxDeadlineDate) {
                    return false;
                }
            }
        }

        // Also check if any existing member's deadline was edited, it must not be empty
        for (const [key, deadline] of Object.entries(stagedDeadlines)) {
            if (!deadline) {
                return false;
            }
            if (maxDeadlineDate && deadline > maxDeadlineDate) {
                return false;
            }
        }

        return true;
    }, [hasChanges, stagedAssignments, stagedDeadlines, maxDeadlineDate]);

    const handleBulkAssign = async () => {
        if (!hasChanges) {
            onClose();
            return;
        }

        const assignmentsToSubmit = [];
        let validationError = false;

        // 1. Gather newly staged assignments
        Object.entries(stagedAssignments).forEach(([sectionId, members]) => {
            members.forEach(m => {
                const key = `${sectionId}-${m.id}`;
                const deadline = stagedDeadlines[key] || stagedDeadlines[sectionId];
                if (!deadline) {
                    validationError = true;
                }
                assignmentsToSubmit.push({
                    section_id: sectionId,
                    assigned_to_user_id: m.id,
                    deadline: deadline || null
                });
            });
        });

        // 2. Gather modified existing assignments
        sections.forEach(section => {
            const sectionId = section.id || section._id;
            const sectionLabel = section.label || section.key;
            const sectionKey = section.key || section.label;

            const existingRaw = assignments[sectionLabel] || assignments[sectionKey] || assignments[sectionId] ||
                assignments[sectionLabel.toLowerCase()] || assignments[sectionKey.toLowerCase()] ||
                section.assigned_to || [];

            existingRaw.forEach(m => {
                let memberId = "";
                if (typeof m === 'object') {
                    memberId = m.id || m._id || m.user_id;
                }
                if (memberId) {
                    const key = `${sectionId}-${memberId}`;
                    const changedDeadline = stagedDeadlines[key];
                    if (changedDeadline && changedDeadline !== formatForDateInput(m.deadline || "")) {
                        assignmentsToSubmit.push({
                            section_id: sectionId,
                            assigned_to_user_id: memberId,
                            deadline: changedDeadline
                        });
                    }
                }
            });
        });

        if (validationError) {
            alert('Deadline is a required field for all assignments. Please select a date.');
            return;
        }

        if (maxDeadlineDate) {
            let hasDateViolations = false;
            assignmentsToSubmit.forEach(assign => {
                if (assign.deadline && assign.deadline > maxDeadlineDate) {
                    hasDateViolations = true;
                }
            });
            if (hasDateViolations) {
                alert(`Section deadlines must be strictly before the Proposal Submission date (${editableData.PROPOSAL_DEADLINE || editableData.proposal_deadline}).`);
                return;
            }
        }

        if (assignmentsToSubmit.length === 0) {
            onClose();
            return;
        }

        setIsAssigning(true);
        try {
            const response = await bulkAssignSections(rfpId, assignmentsToSubmit);

            if (response.status === 200 || response.status === 201) {
                if (typeof fetchOverview === 'function') {
                    await fetchOverview(setAssignments);
                }

                setStagedAssignments({});
                setStagedDeadlines({});

                if (typeof fetchRFPData === 'function') {
                    await fetchRFPData(true);
                }

                if (typeof setSuccessToast === 'function') {
                    setSuccessToast({
                        message: '✓ Section Reassigned Successfully',
                        rfpName: 'Section assignments have been updated.'
                    });
                    setTimeout(() => setSuccessToast(null), 5000);
                }

                onClose();
            } else {
                alert(`Failed to confirm assignments: ${response.data?.detail || 'Server error'}`);
            }
        } catch (error) {
            console.error('Error in bulk assignment:', error);
            const errorMsg = error.response?.data?.detail || 'An error occurred during assignment.';
            alert(errorMsg);
        } finally {
            setIsAssigning(false);
        }
    };

    const formatApprovalDate = (dateStr) => {
        if (!dateStr) return '30/06/2026';
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return '30/06/2026';
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        return `${d}/${m}/${y}`;
    };

    if (!isOpen) return null;

    return (
        <div className="ai-modal-overlay">
            <div className="ai-modal-content assign-modal">
                {isAssigning && (
                    <div className="modal-loading-overlay">
                        <CustomLoader size={32} />
                        <p>Confirming assignments...</p>
                    </div>
                )}

                <div className="ai-modal-header">
                    <div className="ai-modal-title">
                        <Users size={20} color="#FF5526" />
                        <h3>Assign Sections</h3>
                    </div>
                    <button className="modal-close-btn" onClick={onClose} disabled={isAssigning}>
                        <X size={20} />
                    </button>
                </div>

                <div className="ai-modal-body assign-modal-body no-padding">
                    <div className="assignment-table-container">
                        <table className="assignment-table" style={{ borderCollapse: 'separate', background: 'transparent', border: 'none', boxShadow: 'none', borderRadius: '0', marginTop: '0', width: '100%' }}>
                            <thead>
                                <tr>
                                    <th className="col-sl" style={{ width: '40px' }}></th>
                                    <th className="col-section">Section</th>
                                    <th className="col-assignee">Assigned To</th>
                                    <th className="col-deadline">
                                        Deadline <span className="assign-required-star" style={{ color: '#EF4444', marginLeft: '2px', fontWeight: 'bold' }}>*</span>
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {sections.map((section, idx) => {
                                    const sectionId = section.id || section._id;
                                    const sectionLabel = section.label || section.key;
                                    const sectionKey = section.key || section.label;

                                    const existingRaw = section.assigned_users ||
                                        assignments[sectionLabel] || assignments[sectionKey] || assignments[sectionId] ||
                                        assignments[sectionLabel.toLowerCase()] || assignments[sectionKey.toLowerCase()] ||
                                        section.assigned_to || [];
                                    const existing = existingRaw.map(m => {
                                        if (typeof m === 'object') {
                                            const mid = m.id || m._id || m.user_id;
                                            return { ...m, id: mid, name: m.name || `${m.first_name} ${m.last_name}`, isExisting: true };
                                        }
                                        return { name: m, isExisting: true };
                                    });
                                    const staged = stagedAssignments[sectionId] || [];
                                    const activeMembers = [...existing, ...staged];

                                    const isAdding = showDropdownForId === sectionId;

                                    const statusObj = section.status && typeof section.status === 'object' ? section.status : {};
                                    const statusCodeRaw = statusObj.code;
                                    const statusLabelRaw = statusObj.label || (typeof section.status === 'string' ? section.status : '');
                                    let sectionStatus = statusLabelRaw ||
                                        editableData?.SECTION_STATUS?.[sectionKey] || editableData?.section_status?.[sectionKey] ||
                                        editableData?.SECTION_STATUS?.[sectionLabel] || editableData?.section_status?.[sectionLabel] || '';
                                    const statusStr = String(sectionStatus).toLowerCase();
                                    const statusCode = statusCodeRaw || section.status_code || parseInt(sectionStatus) || 0;
                                    const isApproved = statusCode === 54 || statusStr.includes('approved') || statusStr.includes('finalised');
                                    const isSubmitted = statusCode === 53 || (statusStr.includes('submitted') && !statusStr.includes('not submitted'));

                                    return (
                                        <tr key={sectionId || sectionLabel} className={`${staged.length > 0 ? 'row-has-changes' : ''} ${isApproved ? 'row-approved' : ''}`} style={{ background: 'transparent' }}>
                                            <td className="col-sl" style={{ background: isApproved ? '#F0FDF4' : '#FFFFFF', border: 'none', borderTop: isApproved ? '1px solid #DCFCE7' : '1px solid #F1F5F9', borderBottom: isApproved ? '1px solid #DCFCE7' : '1px solid #F1F5F9', borderLeft: isApproved ? '1px solid #DCFCE7' : '1px solid #F1F5F9', borderRadius: '12px 0 0 12px', padding: '14px 16px', verticalAlign: 'middle' }}>
                                                <div className="assign-section-badge">{idx + 1}</div>
                                            </td>
                                            <td className="col-section" style={{ background: isApproved ? '#F0FDF4' : '#FFFFFF', border: 'none', borderTop: isApproved ? '1px solid #DCFCE7' : '1px solid #F1F5F9', borderBottom: isApproved ? '1px solid #DCFCE7' : '1px solid #F1F5F9', padding: '14px 16px', verticalAlign: 'middle' }}>
                                                <div className="assign-name-main">{sectionLabel}</div>
                                                {isApproved && (
                                                    <div className="approved-metadata-text">
                                                        <Check size={12} className="check-icon" />
                                                        <span>Approved by TL on {formatApprovalDate(section.updated_at)}</span>
                                                    </div>
                                                )}
                                                {!isApproved && isSubmitted && (
                                                    <div className="submitted-metadata-text" style={{ fontSize: '11px', color: '#D97706', marginTop: '4px', display: 'flex', alignItems: 'center', gap: '4px', fontWeight: 500 }}>
                                                        <span>Pending approval</span>
                                                    </div>
                                                )}
                                            </td>
                                            <td className="col-assignee" title={isApproved ? "This section is approved by Team Lead" : undefined} style={{ background: isApproved ? '#F0FDF4' : '#FFFFFF', border: 'none', borderTop: isApproved ? '1px solid #DCFCE7' : '1px solid #F1F5F9', borderBottom: isApproved ? '1px solid #DCFCE7' : '1px solid #F1F5F9', padding: '14px 16px', verticalAlign: 'middle' }}>
                                                <div className="assigned-members-stack" style={{ cursor: isApproved ? 'not-allowed' : 'default' }}>
                                                    {activeMembers.map((member, mIdx) => (
                                                        <div key={member.id || mIdx} className={`member-pill-target ${isApproved ? 'read-only' : ''}`} style={{ paddingRight: isApproved ? '12px' : '4px' }}>
                                                            <div className="member-pill-avatar-target-wrapper" style={{ flexShrink: 0, display: 'flex' }}>
                                                                <Avatar name={member.name} size={20} />
                                                            </div>
                                                            <span className="member-pill-name-target">{member.name}</span>
                                                            {!isApproved && (
                                                                <button
                                                                    className="member-pill-remove-target"
                                                                    onClick={isSubmitted ? undefined : () => handleUnassignClick(sectionId, sectionLabel, member)}
                                                                    disabled={isSubmitted}
                                                                    title={isSubmitted ? "Approve or request revision first" : "Unassign member"}
                                                                    style={isSubmitted ? { cursor: 'not-allowed', opacity: 0.4 } : {}}
                                                                >
                                                                    <X size={10} />
                                                                </button>
                                                            )}
                                                        </div>
                                                    ))}

                                                    {isApproved && (
                                                        <div className="approved-badge">
                                                            <Lock size={11} className="lock-icon" />
                                                            <span>Approved</span>
                                                        </div>
                                                    )}

                                                    {isAdding ? (
                                                        <div className="inline-member-select">
                                                            <select
                                                                ref={selectRef}
                                                                className="inline-dropdown"
                                                                onChange={(e) => {
                                                                    const val = e.target.value;
                                                                    if (!val) return;
                                                                    const selected = membersList.find(m => String(m._id || m.id) === val);
                                                                    handleAddMember(section, selected);
                                                                }}
                                                                autoFocus
                                                                onBlur={() => setTimeout(() => setShowDropdownForId(null), 200)}
                                                            >
                                                                {membersList.length === 0 ? (
                                                                    <option value="">No team members</option>
                                                                ) : (
                                                                    <>
                                                                        <option value="">Select Member...</option>
                                                                        {membersList
                                                                            .filter(m => {
                                                                                const mId = m._id || m.id;
                                                                                const mName = m.name || `${m.first_name} ${m.last_name}`;
                                                                                return !activeMembers.some(am => (am.id === mId) || am.name === mName);
                                                                            })
                                                                            .map(member => (
                                                                                <option key={member._id || member.id} value={member._id || member.id}>
                                                                                    {member.name || `${member.first_name} ${member.last_name}`}
                                                                                </option>
                                                                            ))
                                                                        }
                                                                    </>
                                                                )}
                                                            </select>
                                                        </div>
                                                    ) : (
                                                        !isApproved && (
                                                            activeMembers.length === 0 ? (
                                                                <button
                                                                    className="assign-dotted-btn"
                                                                    onClick={() => setShowDropdownForId(sectionId)}
                                                                    disabled={isAssigning}
                                                                >
                                                                    <UserPlus size={14} />
                                                                    <span>Assign</span>
                                                                </button>
                                                            ) : (
                                                                <button
                                                                    className="assign-solid-btn"
                                                                    onClick={() => setShowDropdownForId(sectionId)}
                                                                    disabled={isAssigning}
                                                                >
                                                                    <UserPlus size={14} />
                                                                    <span>Assign</span>
                                                                </button>
                                                            )
                                                        )
                                                    )}
                                                </div>
                                            </td>
                                            <td className="col-deadline" style={{ background: isApproved ? '#F0FDF4' : '#FFFFFF', border: 'none', borderTop: isApproved ? '1px solid #DCFCE7' : '1px solid #F1F5F9', borderBottom: isApproved ? '1px solid #DCFCE7' : '1px solid #F1F5F9', borderRight: isApproved ? '1px solid #DCFCE7' : '1px solid #F1F5F9', borderRadius: '0 12px 12px 0', padding: '14px 16px', verticalAlign: 'middle' }}>
                                                <div className="deadlines-stack" style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                                                    {activeMembers.length === 0 ? (
                                                        <div className="deadline-item" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                            <div className="deadline-input-wrapper-target" onClick={(e) => {
                                                                if (isApproved) return;
                                                                try {
                                                                    e.currentTarget.querySelector('.table-date-input-target')?.showPicker();
                                                                } catch (err) {
                                                                    console.warn('showPicker not supported:', err);
                                                                }
                                                            }} style={{ cursor: isApproved ? 'not-allowed' : 'pointer', opacity: isApproved ? 0.7 : 1 }}>
                                                                <input
                                                                    type="date"
                                                                    className="table-date-input-target"
                                                                    value={stagedDeadlines[sectionId] || ""}
                                                                    onChange={(e) => handleDeadlineChange(sectionId, null, e.target.value)}
                                                                    disabled={isAssigning || isApproved}
                                                                    placeholder="dd/mm/yyyy"
                                                                    min={new Date().toISOString().split('T')[0]}
                                                                    max={maxDeadlineDate}
                                                                    style={{ cursor: isApproved ? 'not-allowed' : 'pointer' }}
                                                                />
                                                                <Calendar size={14} className="calendar-icon-target" />
                                                            </div>
                                                        </div>
                                                    ) : (
                                                        activeMembers.map((member, mIdx) => {
                                                            const memberId = member.id || member._id;
                                                            const stagedKey = `${sectionId}-${memberId}`;
                                                            const mDeadline = stagedDeadlines[stagedKey] !== undefined
                                                                ? stagedDeadlines[stagedKey]
                                                                : (stagedDeadlines[sectionId] !== undefined
                                                                    ? stagedDeadlines[sectionId]
                                                                    : formatForDateInput(member.deadline || ""));

                                                            return (
                                                                <div key={memberId || mIdx} className="deadline-item" style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                                                    {activeMembers.length > 1 && (
                                                                        <span style={{ fontSize: '0.62rem', color: '#64748B', fontWeight: '700' }}>
                                                                            {member.name}'s Deadline <span style={{ color: '#EF4444', marginLeft: '2px' }}>*</span>
                                                                        </span>
                                                                    )}
                                                                    <div className="deadline-input-wrapper-target" onClick={(e) => {
                                                                        if (isApproved) return;
                                                                        try {
                                                                            e.currentTarget.querySelector('.table-date-input-target')?.showPicker();
                                                                        } catch (err) {
                                                                            console.warn('showPicker not supported:', err);
                                                                        }
                                                                    }} style={{ cursor: isApproved ? 'not-allowed' : 'pointer', opacity: isApproved ? 0.7 : 1 }}>
                                                                        <input
                                                                            type="date"
                                                                            className="table-date-input-target"
                                                                            value={mDeadline}
                                                                            onChange={(e) => handleDeadlineChange(sectionId, memberId, e.target.value)}
                                                                            disabled={isAssigning || isApproved}
                                                                            placeholder="dd/mm/yyyy"
                                                                            min={new Date().toISOString().split('T')[0]}
                                                                            max={maxDeadlineDate}
                                                                            style={{ cursor: isApproved ? 'not-allowed' : 'pointer' }}
                                                                        />
                                                                        <Calendar size={14} className="calendar-icon-target" />
                                                                    </div>
                                                                </div>
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>

                <div className="ai-modal-footer-target" style={{ justifyContent: 'space-between' }}>
                    <button className="cancel-assign-btn" onClick={onClose} disabled={isAssigning}>
                        Cancel
                    </button>
                    <button
                        className={`confirm-assignments-btn ${!canConfirm ? 'disabled' : ''}`}
                        onClick={handleBulkAssign}
                        disabled={isAssigning || !canConfirm}
                    >
                        {isAssigning ? <CustomLoader size={16} /> : <Check size={16} />}
                        <span>Confirm Assignments</span>
                    </button>
                </div>
            </div>

            {/* Confirm Unassign Modal */}
            {unassignModal.show && (
                <div className="custom-modal-overlay" style={{ zIndex: 1100 }}>
                    <div className="stage-modal-content" style={{ maxWidth: '400px', textAlign: 'center' }}>
                        <div className="stage-modal-header" style={{ padding: '1.5rem 1.5rem 0.5rem 1.5rem' }}>
                            <h3 style={{ fontSize: '1.1rem', fontWeight: '700' }}>Confirm Unassignment</h3>
                        </div>
                        <div className="stage-modal-body" style={{ padding: '0.3rem 1.5rem 0.3rem 1.5rem' }}>
                            <p style={{ color: '#64748B', fontSize: '0.9rem', lineHeight: '1.5' }}>
                                Are you sure you want to delete <strong>{unassignModal.memberName}</strong> from <strong>{unassignModal.sectionLabel}</strong> section?
                            </p>
                        </div>
                        <div className="stage-modal-actions" style={{ padding: '1rem 1.5rem 1.5rem 1.5rem', display: 'flex', gap: '0.75rem', justifyContent: 'center' }}>
                            <button
                                className="stage-modal-cancel"
                                style={{ flex: 1, padding: '0.6rem', background: 'white', border: '1px solid #E2E8F0', borderRadius: '8px', color: '#64748B', fontWeight: '600' }}
                                onClick={() => setUnassignModal({ ...unassignModal, show: false })}
                                disabled={isAssigning}
                            >
                                Close
                            </button>
                            <button
                                className="stage-modal-confirm"
                                style={{ flex: 1, padding: '0.6rem', background: '#EF4444', border: 'none', borderRadius: '8px', color: 'white', fontWeight: '600' }}
                                onClick={handleConfirmUnassign}
                                disabled={isAssigning}
                            >
                                {isAssigning ? <CustomLoader size={16} /> : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SectionAssignModal;
